import { StakeholderPermissionClaims } from '@freetech/models/user';

/**
 * Create an empty stakeholder permissions object with all permissions set to false
 */
export function createEmptyStakeholderPermissions(): StakeholderPermissionClaims {
  return {
    projects: {
      viewer: false,
      admin: false,
    },
    freelo: {
      view: false,
      admin: false,
    },
    billing: {
      view: false,
      admin: false,
    },
    contracts: {
      view: false,
      admin: false,
    },
    meetings: {
      view: false,
      admin: false,
    },
  };
}

/**
 * Basic Stakeholder permissions - read-only access
 */
export const BASIC_STAKEHOLDER_PERMISSIONS: StakeholderPermissionClaims = {
  projects: {
    viewer: true,
    admin: false,
  },
  freelo: {
    view: true,
    admin: false,
  },
  billing: {
    view: true,
    admin: false,
  },
  contracts: {
    view: true,
    admin: false,
  },
  meetings: {
    view: true,
    admin: false,
  },
};

/**
 * Admin Stakeholder permissions - full access within stakeholder scope
 */
export const ADMIN_STAKEHOLDER_PERMISSIONS: StakeholderPermissionClaims = {
  projects: {
    viewer: true,
    admin: true,
  },
  freelo: {
    view: true,
    admin: true,
  },
  billing: {
    view: true,
    admin: true,
  },
  contracts: {
    view: true,
    admin: true,
  },
  meetings: {
    view: true,
    admin: true,
  },
};

/**
 * Get all stakeholder permission groups
 */
export function getAllStakeholderPermissionGroups(): string[] {
  const empty = createEmptyStakeholderPermissions();
  return Object.keys(empty);
}

/**
 * Get all permissions for a specific stakeholder group/category
 */
export function getStakeholderPermissionsForGroup(group: keyof StakeholderPermissionClaims): string[] {
  const empty = createEmptyStakeholderPermissions();
  const groupPermissions = empty[group];
  
  if (typeof groupPermissions === 'object' && groupPermissions !== null) {
    return Object.keys(groupPermissions);
  }
  
  return [];
}

/**
 * Check if a stakeholder permission value is boolean or has a different type
 */
export function getStakeholderPermissionType(group: keyof StakeholderPermissionClaims, permission: string): 'boolean' | 'select' | 'array' {
  const empty = createEmptyStakeholderPermissions();
  const value = (empty[group] as any)?.[permission];
  
  if (typeof value === 'boolean') {
    return 'boolean';
  } else if (Array.isArray(value)) {
    return 'array';
  } else {
    return 'select';
  }
}

/**
 * Get options for select-type stakeholder permissions
 */
export function getStakeholderPermissionOptions(group: keyof StakeholderPermissionClaims, permission: string): string[] {

  return [];
}

/**
 * Get default stakeholder permissions for role
 */
export function getDefaultStakeholderPermissionsForRole(role: string): StakeholderPermissionClaims {
  switch (role) {
    case 'stakeholder':
      return BASIC_STAKEHOLDER_PERMISSIONS;
    default:
      return createEmptyStakeholderPermissions();
  }
}

/**
 * Helper function to create a stakeholder permission template with specific overrides
 */
export function createStakeholderPermissionTemplate(
  baseTemplate: StakeholderPermissionClaims = createEmptyStakeholderPermissions(),
  overrides: Partial<StakeholderPermissionClaims> = {}
): StakeholderPermissionClaims {
  const result = { ...baseTemplate };
  

  Object.keys(overrides).forEach(key => {
    const groupKey = key as keyof StakeholderPermissionClaims;
    if (overrides[groupKey]) {
      result[groupKey] = {
        ...result[groupKey],
        ...overrides[groupKey]
      } as any;
    }
  });
  
  return result;
}

/**
 * Validate stakeholder permissions object structure
 */
export function validateStakeholderPermissions(permissions: any): permissions is StakeholderPermissionClaims {
  if (!permissions || typeof permissions !== 'object') {
    return false;
  }
  
  const requiredGroups = getAllStakeholderPermissionGroups();
  
  return requiredGroups.every(group => {
    return group in permissions && typeof permissions[group] === 'object';
  });
} 